import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { useDebouncedCallback } from 'use-debounce';

import { CfsApi, KaseApi, TaskApi } from '@/api';
import { IcClickout } from '@/assets/images';
import { NoResultItem } from '@/components';
import { DATE_TIME_FORMAT } from '@/config';
import { KaseRoleBasedAction, LocalStorageItems } from '@/constants';
import { useKaseContext } from '@/hooks';
import { ICFS, IKase, IKaseCfsLink, IKaseKaseLink, ITask } from '@/models';
import {
  getAddressLocation,
  isReadOnlyKaseRole,
  validateRoute,
} from '@/services';
import { useToastStore, useUserStore } from '@/store';
import { colors } from '@/theme/variables';
import { openNewWindow } from '@/utils';

import { RecordTypePill } from './style';

interface RelatedSearchProps {
  linkedMastertCFS: (IKaseCfsLink | IKaseKaseLink | IKase | ICFS | ITask)[];
  makeLink: (id: string, type: 'cfs' | 'kase' | 'task', newValue?: any) => void;
  forceEnable?: boolean;
  initialData?: ((ICFS | IKase | ITask) & {
    recordType: 'cfs' | 'kase' | 'task';
  })[];
}

type RelatedSearchItem = (ICFS | IKase | ITask) & {
  recordType: 'cfs' | 'kase' | 'task';
};

const PILL_LABEL: Record<string, string> = {
  cfs: 'CFS',
  kase: 'Case',
  task: 'Task',
};

const OPEN_URL: Record<string, (id: string) => string> = {
  task: (id) => `/tasks/${id}`,
  cfs: (id) => `/cfs/${id}`,
  kase: (id) => `/cases/${id}/overview`,
};

const RelatedSearch: React.FC<RelatedSearchProps> = ({
  linkedMastertCFS,
  makeLink,
  forceEnable = false,
  initialData,
}: RelatedSearchProps) => {
  const { account, user } = useUserStore.getState();
  const [searchText, setSearchText] = useState('');
  const [searchedLinks, setSearchedLinks] = useState<RelatedSearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { updateToast } = useToastStore();
  const { kase } = useKaseContext();
  const urlParams = useParams();

  const taskId = urlParams.taskId !== 'new' ? urlParams.taskId : undefined;
  const fetchIdRef = useRef(0);

  const handleChange = async (newValue: RelatedSearchItem) => {
    if (!newValue._id) return;
    setSearchText('');
    makeLink(newValue._id, newValue.recordType, newValue);
  };

  useEffect(() => {
    if (initialData?.length) {
      setSearchedLinks(initialData);
    } else {
      fetchRelatedItems();
    }
  }, []);

  const handleInputChange = useDebouncedCallback(
    (_: SyntheticEvent, v: string) => {
      setSearchText(v);
      fetchRelatedItems(v);
    },
    500,
  );

  const fetchRelatedItems = (searchString?: string) => {
    setLoading(true);
    setSearchedLinks([]);
    const fetchId = ++fetchIdRef.current;

    const params = {
      sort: JSON.stringify({ createdAt: -1 }),
      limit: 50,
      filter: searchString
        ? JSON.stringify({
            items: [],
            logicOperator: 'and',
            quickFilterValues: [searchString],
            quickFilterLogicOperator: 'and',
          })
        : '',
    };

    const currentAgencyId = localStorage.getItem(
      LocalStorageItems.CurrentAgencyId,
    );
    const canGetKase =
      !!currentAgencyId &&
      validateRoute('/cases', user || undefined, account || undefined);

    const promises: Promise<void>[] = [];

    promises.push(
      CfsApi.list(params)
        .then((resCfs) => {
          if (fetchId !== fetchIdRef.current) {
            return;
          }
          const items = resCfs.data.docs.map((cfs) => ({
            ...cfs,
            recordType: 'cfs' as const,
          }));
          setSearchedLinks((prev) => [...prev, ...items]);
        })
        .catch((err) => {
          if (err?.status === 403) return;
          updateToast({ open: true, message: err?.message });
        }),
    );

    if (canGetKase) {
      promises.push(
        KaseApi.list(params)
          .then((resKase) => {
            if (fetchId !== fetchIdRef.current) {
              return;
            }
            const items = resKase.data.results.map((k) => ({
              ...k,
              recordType: 'kase' as const,
            }));
            setSearchedLinks((prev) => [...prev, ...items]);
          })
          .catch((err) => {
            if (err?.status === 403) return;
            updateToast({ open: true, message: err?.message });
          }),
      );
    }

    if (currentAgencyId) {
      promises.push(
        TaskApi.getTasks(params)
          .then((resTask) => {
            if (fetchId !== fetchIdRef.current) {
              return;
            }
            const allTasks = taskId
              ? resTask.data.docs?.filter((tsk) => tsk._id !== taskId)
              : resTask.data.docs;
            const items = (allTasks || []).map((t) => ({
              ...t,
              recordType: 'task' as const,
            }));
            setSearchedLinks((prev) => [...prev, ...items]);
          })
          .catch((err) => {
            if (err?.status === 403) return;
            updateToast({ open: true, message: err?.message });
          }),
      );
    }

    Promise.allSettled(promises).then(() => setLoading(false));
  };

  return (
    <Autocomplete
      fullWidth
      autoHighlight
      openOnFocus
      loading={loading}
      loadingText="Loading..."
      onInputChange={handleInputChange}
      options={searchedLinks}
      noOptionsText={<NoResultItem />}
      filterOptions={(filteredCases) => {
        return filteredCases.reduce((acc, item) => {
          const found = linkedMastertCFS.find((linked) => {
            return (
              (linked as IKaseCfsLink)?.cfs?._id === item._id ||
              (linked as IKaseKaseLink)?.linked?._id === item._id ||
              (linked as ICFS | IKase | ITask)?._id === item._id
            );
          });
          return found ? acc : [...acc, item];
        }, [] as RelatedSearchItem[]);
      }}
      onChange={(_, v) => {
        if (v) handleChange(v);
      }}
      value={null}
      getOptionLabel={() => ''}
      disabled={
        forceEnable
          ? false
          : isReadOnlyKaseRole(KaseRoleBasedAction.OVERVIEW, user, kase)
      }
      renderOption={({ key, ...props }, item) => {
        const { number, createdAt } = item as IKase | ICFS;
        const { addressInfo, shortDescription = '' } = item as ICFS;
        const { address } = addressInfo || {};
        const { taskID, title } = item as ITask;
        const recordType = item.recordType;

        const handleOpen = (e: React.MouseEvent) => {
          e.stopPropagation();
          if (item._id) openNewWindow(OPEN_URL[recordType](item._id));
        };

        return (
          <Box
            key={key}
            component="li"
            {...props}
            sx={{
              borderBottom: `1px solid ${colors.grey[10]}`,
              p: '8px 12px !important',
              '&:hover .link-btn': { display: 'inline-flex' },
            }}
          >
            <Stack
              flexDirection="row"
              alignItems="center"
              sx={{ width: '100%', gap: 1 }}
            >
              <RecordTypePill label={PILL_LABEL[recordType]} size="small" />
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {number || taskID}
                  {createdAt && (
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ fontWeight: 400, ml: 0.5, color: 'text.secondary' }}
                    >
                      · {moment(createdAt).format(DATE_TIME_FORMAT)}
                    </Typography>
                  )}
                </Typography>
                {(address || shortDescription || title) && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                    noWrap
                  >
                    {address
                      ? getAddressLocation(address)
                      : title || shortDescription}
                  </Typography>
                )}
              </Stack>
              <Stack flexDirection="row" alignItems="center" sx={{ gap: 0.5 }}>
                <Button
                  className="link-btn"
                  variant="text"
                  color="error"
                  size="small"
                  sx={{ display: 'none', minWidth: 0, px: 1 }}
                >
                  Link
                </Button>
                <IconButton
                  size="small"
                  onClick={handleOpen}
                  sx={{ color: 'text.secondary', flexShrink: 0 }}
                >
                  <IcClickout style={{ width: 16, height: 16 }} />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          value={searchText}
          placeholder="Search..."
          fullWidth
        />
      )}
      clearOnBlur={false}
    />
  );
};

export default RelatedSearch;
